/**
 * A report as data: what goes on the pages, not how it is drawn. The backtest
 * and live-bot reports build one of these; `render.ts` turns it into a PDF.
 * Kept free of jsPDF so building a report costs nothing until it is drawn.
 */

export type Tone = "up" | "down" | undefined;

export interface ReportStat {
  label: string;
  value: string;
  tone?: Tone;
}

export interface ReportCell {
  text: string;
  tone?: Tone;
}

export interface ReportSeries {
  label: string;
  /** [time ms, value] */
  points: [number, number][];
  tone?: "strategy" | "benchmark" | "drawdown";
  /** Shade between the line and the chart's baseline. */
  fill?: boolean;
}

export interface ReportChart {
  kind: "chart";
  title: string;
  series: ReportSeries[];
  format: (value: number) => string;
  formatTime: (ms: number) => string;
  /** A dashed reference line: starting capital, or zero. */
  baseline?: number;
  height?: number;
}

export interface ReportTable {
  kind: "table";
  title: string;
  head: string[];
  rows: ReportCell[][];
  align?: ("left" | "right")[];
  note?: string;
  empty?: string;
  fontSize?: number;
}

export type ReportSection =
  | { kind: "stats"; title: string; items: ReportStat[] }
  | ReportChart
  | ReportTable
  | { kind: "text"; title: string; lines: { label?: string; text: string }[] };

export interface ReportDoc {
  /** The small line above the title: "Backtest report". */
  kicker: string;
  title: string;
  subtitle?: string;
  generatedAt: string;
  meta: [label: string, value: string][];
  sections: ReportSection[];
  footer: string;
  /** Parts of the filename, slugged and dated. */
  filename: (string | null | undefined)[];
}

export function cell(text: string, tone?: Tone): ReportCell {
  return { text, tone };
}

export function toneOf(value: number | null | undefined): Tone {
  if (value === null || value === undefined || !Number.isFinite(value) || value === 0) return undefined;
  return value > 0 ? "up" : "down";
}
